<script setup>
import deviceHasHover from '@/helpers/deviceFeatures/deviceHasHover.js'
import { computed, onMounted, ref, useAttrs, useSlots } from 'vue'
import { useMutationObserver } from '@vueuse/core'
import { usePreferencesStore } from '@/stores/usePreferencesStore.js'

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const emit = defineEmits([ 'click' ])
const preferences = usePreferencesStore()
const props = defineProps({
	route: { type: [ Object, String ], default: null },
	loading: { type: Boolean, default: false },
	icon: { type: [ String, Array ], default: null },
	context: { type: String, default: null },
	hideIcon: { type: Boolean, default: false },
	hideText: { type: Boolean, default: false },
})
const slots = useSlots()

const rootEl = ref(null)
const textEl = ref(null)
const text = ref(null)

const type = computed(() => { return props?.route ? 'router-link' : 'button' })
const hasText = computed(() => { return (slots?.default?.()?.length ?? 0) > 0 })
const hasIcon = computed(() => { return props.icon !== null })
const showIcon = computed(() => {
	const userPrefersTextOnly = preferences.get('buttons') === 'text_only'
	// do not show the icon by default
	let show = false
	if (hasIcon.value) {
		// if there is an icon, show it by default
		show = true
		// if the users prefers text, do not show the icon
		if (userPrefersTextOnly) { show = false }
		// if the button is set to hide the icon, do not show the icon
		if (props.hideIcon) { show = false }
		// if there is no text, show the icon
		if (!hasText.value) { show = true }
	}
	return show
})
const showText = computed(() => {
	const userPrefersIconOnly = preferences.get('buttons') === 'icon_only'
	// do not show text by default
	let show = false
	if (hasText.value) {
		// if there is text, show it by default
		show = true
		// if the users prefers icons, do not show the text
		if (userPrefersIconOnly) { show = false }
		// if the button is set to hide text, do not show the text
		if (props.hideText) { show = false }
		// if there is no icon, show the text
		if (!hasIcon.value) { show = true }
		// show it if the device has no hover
		if (!deviceHasHover()) { show = true }
		// show it if for some reason hideText AND hideIcon are on
		if (props.hideIcon && props.hideText) { show = true }
	}
	return show
})

onMounted(() => {
	text.value = textEl.value.innerText
	useMutationObserver(
		rootEl,
		() => { text.value = textEl.value.innerText },
		{ characterData: true, subtree: true }
	)
})
</script>

<template>
	<component
		:is="type"
		ref="rootEl"
		v-tip="(hasText && !showText && !attrs['v-tip']) ? text : false"
		:aria-label="hasText && !showText ? text : null"
		:class="context ? `btn-${context}` : ''"
		:title="(hasText && !showText) ? text : null"
		:to="type === 'router-link' ? route : null"
		:type="type === 'button' ? (attrs?.type ?? 'button') : null"
		class="btn"
		role="button"
		v-bind="attrs"
		@click="type==='button' ? emit('click') : null"
	>
		<Transition name="loader-slide">
			<span
				v-if="loading"
				class="loader-slide"
			>
				<font-awesome-icon
					icon="circle-notch"
					spin
				/>
			</span>
		</Transition>
		<font-awesome-icon
			v-if="showIcon"
			:icon="icon"
			fixed-width
		/>
		<span
			v-show="showText"
			ref="textEl"
			class="text-nowrap"
		>
			<slot />
		</span>
	</component>
</template>
